import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { VoiceNavigationIntent } from '../components/lesson-plan/voiceNavigation';
import ApiClient from '../config/ApiClient';
import { LessonPlanSessionModel, SessionSlideModel } from '../object-actions/types/types';
import { useVoiceLiveSession, VoiceLiveSessionConfig } from './useVoiceLiveSession';

const VOICE_LESSON_DEBUG_PREFIX = '[voice-lesson]';

function voiceLessonDebug(message: string, ...data: unknown[]) {
    console.debug(VOICE_LESSON_DEBUG_PREFIX, message, ...data);
}

export type VoiceLessonPhase =
    | 'idle'
    | 'connecting'
    | 'facilitating'
    | 'listening'
    | 'processing'
    | 'completed'
    | 'error';

export interface VoiceLessonTranscriptEvent {
    id: string;
    speaker: 'ai' | 'participant' | 'system';
    text: string;
    createdAt: string;
}

interface SlideInstructionEntry {
    slide_id: number;
    slide_number: number;
    instructions: string;
}

interface VoiceLessonTokenResponse {
    key: string;
    endpoint: string;
    model: string;
    baseline_prompt: string;
    slide_instructions: SlideInstructionEntry[];
    start_slide_id: number | null;
    tools: VoiceLiveSessionConfig['tools'];
    voice_session_id: number;
    role: string;
}

interface VoiceLessonProgressResponse {
    success: boolean;
    session_id?: number;
    slide_id?: number;
    status?: string;
    started_at?: string | null;
    completed_at?: string | null;
    time_spent_seconds?: number | null;
    voice_session_completed?: boolean;
}

type SlideProgressStatus = 'started' | 'completed' | 'session_completed';

/**
 * Tolerant matchers for participant voice cues that drive slide progression.
 * Run only against the participant transcript (never the AI transcript) so
 * the AI cannot self-advance by saying "next slide" out loud.
 */
const ADVANCE_PHRASE_REGEX = /\b(next slide|next page|move on|let'?s continue|i'?m done|i am done|ready (to )?(move on|continue))\b/i;
const END_PHRASE_REGEX = /\b(end session|finish session|we'?re done|we are done|stop session|done with the session)\b/i;

function errorMessage(err: unknown, fallback: string): string {
    return err instanceof Error ? err.message : fallback;
}

interface UseVoiceLessonSessionOptions {
    lessonPlanId: number | null;
    session: LessonPlanSessionModel;
    onSlideProgress?: (slideId: number, updates: Partial<SessionSlideModel>) => void;
    onSessionProgress?: (sessionId: number, updates: Partial<LessonPlanSessionModel>) => void;
    onNavigationIntent?: (intent: VoiceNavigationIntent) => void;
}

export function useVoiceLessonSession({
    lessonPlanId,
    session,
    onSlideProgress,
    onSessionProgress,
    onNavigationIntent,
}: UseVoiceLessonSessionOptions) {
    const [voiceActive, setVoiceActive] = useState(false);
    const [phase, setPhase] = useState<VoiceLessonPhase>('idle');
    const [voiceError, setVoiceError] = useState<string | null>(null);
    const [voiceSessionId, setVoiceSessionId] = useState<number | null>(null);
    const [transcriptEvents, setTranscriptEvents] = useState<VoiceLessonTranscriptEvent[]>([]);
    const [currentAiTranscript, setCurrentAiTranscript] = useState('');
    const [currentSlideId, setCurrentSlideId] = useState<number | null>(null);

    const isActiveRef = useRef(false);
    const voiceSessionIdRef = useRef<number | null>(null);
    const currentAiTranscriptRef = useRef('');
    const currentSlideIdRef = useRef<number | null>(null);
    const baselinePromptRef = useRef<string>('');
    const slideInstructionsRef = useRef<SlideInstructionEntry[]>([]);
    const slideOrderRef = useRef<number[]>([]);
    const advancingRef = useRef(false);

    useEffect(() => { isActiveRef.current = voiceActive; }, [voiceActive]);
    useEffect(() => { voiceSessionIdRef.current = voiceSessionId; }, [voiceSessionId]);
    useEffect(() => { currentAiTranscriptRef.current = currentAiTranscript; }, [currentAiTranscript]);
    useEffect(() => { currentSlideIdRef.current = currentSlideId; }, [currentSlideId]);

    const {
        connect,
        disconnect: disconnectSession,
        sendSessionUpdate,
        cancelResponse,
        requestResponse,
        setCallbacks,
    } = useVoiceLiveSession();

    const slideOrder = useMemo<number[]>(
        () => (session?.slides || [])
            .filter((slide): slide is SessionSlideModel => Boolean(slide?.id))
            .sort((a, b) => (a.slide_number ?? 0) - (b.slide_number ?? 0))
            .map(slide => slide.id),
        [session?.slides],
    );

    const totalSlides = slideOrder.length;
    const activeSlideIndex = useMemo(
        () => (currentSlideId ? slideOrder.indexOf(currentSlideId) : -1),
        [currentSlideId, slideOrder],
    );

    const addTranscriptEvent = useCallback((speaker: VoiceLessonTranscriptEvent['speaker'], text: string) => {
        const trimmed = text.trim();
        if (!trimmed) return;
        setTranscriptEvents(prev => [
            ...prev,
            {
                id: `${Date.now()}-${prev.length}`,
                speaker,
                text: trimmed,
                createdAt: new Date().toISOString(),
            },
        ]);
    }, []);

    const logInteraction = useCallback(async (
        eventType: string,
        payload: Record<string, unknown> = {},
    ) => {
        const id = voiceSessionIdRef.current;
        if (!lessonPlanId || !id) return;
        try {
            await ApiClient.post(`/api/lesson-plan/${lessonPlanId}/voice-interaction/`, {
                voice_session_id: id,
                event_type: eventType,
                slide_id: currentSlideIdRef.current,
                ...payload,
            }, { 'Content-Type': 'application/json' });
        } catch (err) {
            voiceLessonDebug('interaction log failed', err);
        }
    }, [lessonPlanId]);

    const recordSlideProgress = useCallback(async (
        slideId: number | null,
        status: SlideProgressStatus,
        transcript?: string,
    ): Promise<VoiceLessonProgressResponse | null> => {
        const id = voiceSessionIdRef.current;
        if (!lessonPlanId || !id) return null;
        try {
            const res = await ApiClient.post<VoiceLessonProgressResponse>(
                `/api/lesson-plan/${lessonPlanId}/voice-progress/`,
                {
                    voice_session_id: id,
                    slide_id: slideId,
                    status,
                    transcript: transcript ?? '',
                },
                { 'Content-Type': 'application/json' },
            );
            if (!res.success || !res.data) {
                throw new Error(res.error || 'Failed to record lesson progress');
            }
            if (status !== 'session_completed' && res.data.slide_id) {
                const updates: Partial<SessionSlideModel> = {};
                if (res.data.started_at) updates.started_at = res.data.started_at;
                if (res.data.completed_at) updates.completed_at = res.data.completed_at;
                if (res.data.time_spent_seconds != null) {
                    updates.time_spent_seconds = res.data.time_spent_seconds;
                }
                onSlideProgress?.(res.data.slide_id, updates);
            }
            if (status === 'session_completed' && res.data.session_id) {
                onSessionProgress?.(res.data.session_id, {
                    completed_at: res.data.completed_at || new Date().toISOString(),
                });
            }
            return res.data;
        } catch (err) {
            const message = errorMessage(err, 'Failed to record lesson progress');
            voiceLessonDebug('progress record failed', err);
            setVoiceError(message);
            return null;
        }
    }, [lessonPlanId, onSlideProgress, onSessionProgress]);

    const findSlideEntry = useCallback((slideId: number | null): SlideInstructionEntry | null => {
        if (slideId == null) return null;
        return slideInstructionsRef.current.find(entry => entry.slide_id === slideId) || null;
    }, []);

    const sendInstructionsForSlide = useCallback((slideId: number) => {
        const entry = findSlideEntry(slideId);
        if (!entry) {
            voiceLessonDebug('no instructions found for slide', { slideId });
            return;
        }
        const combined = `${baselinePromptRef.current}\n\n${entry.instructions}`;
        sendSessionUpdate(combined);
    }, [findSlideEntry, sendSessionUpdate]);

    const endSession = useCallback(async (
        source: 'voice' | 'button' | 'auto',
        finalSlideId?: number | null,
    ) => {
        if (advancingRef.current) return;
        advancingRef.current = true;
        try {
            const slideId = finalSlideId ?? currentSlideIdRef.current ?? null;
            cancelResponse();
            const result = await recordSlideProgress(slideId, 'session_completed', source);
            await logInteraction('session_ended', { success: result?.success !== false, error: result ? '' : 'progress record failed' });
            setPhase('completed');
            setVoiceActive(false);
            isActiveRef.current = false;
            disconnectSession();
        } finally {
            advancingRef.current = false;
        }
    }, [cancelResponse, recordSlideProgress, logInteraction, disconnectSession]);

    const advanceToNextSlide = useCallback(async () => {
        if (advancingRef.current) return;
        const order = slideOrderRef.current;
        const currentSlide = currentSlideIdRef.current;
        if (!currentSlide || order.length === 0) return;

        const idx = order.indexOf(currentSlide);
        if (idx < 0) return;

        advancingRef.current = true;
        try {
            setPhase('processing');
            const completedResult = await recordSlideProgress(currentSlide, 'completed');
            if (!completedResult) {
                setPhase('error');
                return;
            }

            const nextSlideId = order[idx + 1];
            if (nextSlideId == null) {
                advancingRef.current = false;
                await endSession('auto', currentSlide);
                return;
            }

            cancelResponse();
            const startedResult = await recordSlideProgress(nextSlideId, 'started');
            if (!startedResult) {
                setPhase('error');
                return;
            }
            setCurrentSlideId(nextSlideId);
            currentSlideIdRef.current = nextSlideId;
            sendInstructionsForSlide(nextSlideId);
            requestResponse();
            setPhase('listening');
        } finally {
            advancingRef.current = false;
        }
    }, [recordSlideProgress, cancelResponse, sendInstructionsForSlide, requestResponse, endSession]);

    const handleParticipantTranscript = useCallback((text: string) => {
        if (!isActiveRef.current) {
            voiceLessonDebug('not active, skipping transcript', text);
            return;
        }
        if (END_PHRASE_REGEX.test(text)) {
            voiceLessonDebug('end phrase detected', text);
            void endSession('voice');
            return;
        }
        if (ADVANCE_PHRASE_REGEX.test(text)) {
            voiceLessonDebug('advance phrase detected', text);
            if (session?.id) {
                onNavigationIntent?.({
                    direction: 'next',
                    source: 'voice',
                    sessionId: session.id,
                    slideId: currentSlideIdRef.current,
                });
            }
            void advanceToNextSlide();
        }
    }, [advanceToNextSlide, endSession, onNavigationIntent, session?.id]);

    const startVoiceLesson = useCallback(async () => {
        if (!lessonPlanId || !session?.id) return;

        setPhase('connecting');
        setVoiceError(null);
        setTranscriptEvents([]);
        setCurrentAiTranscript('');
        setCurrentSlideId(null);
        currentSlideIdRef.current = null;

        try {
            const tokenRes = await ApiClient.get<VoiceLessonTokenResponse>(
                `/api/lesson-plan/${lessonPlanId}/voice-token/?session_id=${session.id}`,
            );
            if (!tokenRes.success || !tokenRes.data) {
                throw new Error(tokenRes.error || 'Failed to start lesson facilitator');
            }

            const {
                key,
                endpoint,
                model,
                baseline_prompt,
                slide_instructions,
                start_slide_id,
                tools,
                voice_session_id,
            } = tokenRes.data;

            if (!slide_instructions.length || start_slide_id == null) {
                throw new Error('Lesson plan session has no slides to facilitate');
            }

            baselinePromptRef.current = baseline_prompt;
            slideInstructionsRef.current = slide_instructions;
            slideOrderRef.current = slide_instructions.map(entry => entry.slide_id);
            setVoiceSessionId(voice_session_id);
            voiceSessionIdRef.current = voice_session_id;

            setCallbacks({
                onSessionCreated: () => {
                    voiceLessonDebug('session created');
                    setPhase('listening');
                },
                onTranscriptDone: (text: string) => {
                    voiceLessonDebug('participant transcript', text);
                    addTranscriptEvent('participant', text);
                    void logInteraction('user_transcript', { transcript: text });
                    handleParticipantTranscript(text);
                },
                onTranscriptDelta: (text: string) => {
                    currentAiTranscriptRef.current += text;
                    setCurrentAiTranscript(currentAiTranscriptRef.current);
                },
                // Commit AI lines only on response.audio_transcript.done so each
                // utterance is one row; output_audio_buffer.stopped can fire many
                // times per response and was fragmenting the transcript.
                onAudioTranscriptDone: (text: string) => {
                    voiceLessonDebug('ai transcript done', text);
                    addTranscriptEvent('ai', text);
                    void logInteraction('ai_transcript', { transcript: text });
                    currentAiTranscriptRef.current = '';
                    setCurrentAiTranscript('');
                },
                // No onFunctionCall: the facilitator registers no tools, so the
                // AI has no function-call channel and cannot mutate progress.
                onAudioOutputStarted: () => {
                    currentAiTranscriptRef.current = '';
                    setCurrentAiTranscript('');
                    setPhase('facilitating');
                },
                onAudioOutputStopped: () => {
                    if (isActiveRef.current && phase !== 'completed') setPhase('listening');
                },
                onError: (error: string) => {
                    setVoiceError(error);
                    setPhase('error');
                    void logInteraction('error', { error, success: false });
                },
                onDisconnected: () => {
                    if (isActiveRef.current) {
                        setVoiceError('Lesson facilitator disconnected unexpectedly');
                        setPhase('error');
                    }
                },
            });

            const startEntry = slide_instructions.find(entry => entry.slide_id === start_slide_id)
                || slide_instructions[0];
            const initialPrompt = `${baseline_prompt}\n\n${startEntry.instructions}`;

            const config: VoiceLiveSessionConfig = {
                key,
                endpoint,
                model,
                systemPrompt: initialPrompt,
                tools,
            };

            await connect(config);
            setCurrentSlideId(startEntry.slide_id);
            currentSlideIdRef.current = startEntry.slide_id;
            setVoiceActive(true);
            isActiveRef.current = true;
            void recordSlideProgress(startEntry.slide_id, 'started');
        } catch (err: unknown) {
            const message = errorMessage(err, 'Failed to start lesson facilitator');
            voiceLessonDebug('start failed', err);
            setVoiceError(message);
            setPhase('error');
        }
    }, [lessonPlanId, session?.id, connect, setCallbacks, addTranscriptEvent, logInteraction, handleParticipantTranscript, recordSlideProgress, phase]);

    const stopVoiceLesson = useCallback(async () => {
        if (phase === 'completed') {
            disconnectSession();
            setVoiceActive(false);
            setPhase('idle');
            setCurrentAiTranscript('');
            return;
        }
        await endSession('button');
        setPhase('idle');
        setCurrentAiTranscript('');
    }, [endSession, disconnectSession, phase]);

    useEffect(() => () => {
        disconnectSession();
    }, [disconnectSession]);

    return {
        voiceActive,
        phase,
        voiceError,
        voiceSessionId,
        transcriptEvents,
        currentAiTranscript,
        currentSlideId,
        activeSlideIndex,
        totalSlides,
        startVoiceLesson,
        stopVoiceLesson,
        advanceToNextSlide,
        endSession,
    };
}
